import { memo, useCallback, useMemo, useState } from 'react';
import { Avatar, Badge, Button, DataTable, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Shield, Trash2, TriangleAlert, Users } from 'lucide-react';

import { PERMISSIONS } from '../../constants/permissions';
import { useRoleMembers } from '../../hooks/useRoleMembers';
import { useRoleMutations } from '../../hooks/useRoleMutations';
import { getAvatarColorClass } from '../../utils/avatarColor';
import { formatCell } from '../../utils/formatCell';
import { getInitials, isPlaceholderProfile } from '../../utils/memberDisplay';
import ConfirmActionModal from '../member-detail/ConfirmActionModal';
import Can from '../guards/Can';
import StatusBadge from '../shared/StatusBadge';
import AssignRoleMembersModal from './AssignRoleMembersModal';

/**
 * Members tab of the role detail, as in the prototype: "N members" with Assign members, and a table
 * of who holds the role (job title, MFA, status) with a trash button per row. Assigning picks from
 * the members who do not have the role yet; removing asks first. Both need the manage permission.
 *
 * @param {object} props
 * @param {object} props.role UI role (see `mapApiRoleToUi`)
 * @param {() => void} props.onChanged Called after a member was assigned or removed (refreshes the counts).
 */
function RoleMembersTab({ role, onChanged }) {
  const { t } = useTranslation('people-access');
  const { members, isLoading, error, reload } = useRoleMembers(role.id);
  const { assignMember, unassignMember } = useRoleMutations();
  const [isAssigning, setIsAssigning] = useState(false);
  const [toRemove, setToRemove] = useState(null);

  const memberIds = useMemo(() => members.map((member) => member.id), [members]);

  const refresh = useCallback(() => {
    reload();
    onChanged();
  }, [reload, onChanged]);

  const openAssign = useCallback(() => setIsAssigning(true), []);
  const closeAssign = useCallback(() => setIsAssigning(false), []);
  const closeRemove = useCallback(() => setToRemove(null), []);

  const handleAssign = useCallback(
    async (member) => {
      const ok = await assignMember(role, { id: member.id, displayName: member.name });
      if (ok) refresh();
      return ok;
    },
    [assignMember, role, refresh],
  );

  const handleRemove = useCallback(async () => {
    const ok = await unassignMember(role, toRemove);
    if (ok) refresh();
    return ok;
  }, [unassignMember, role, toRemove, refresh]);

  const columns = useMemo(
    () => [
      {
        key: 'displayName',
        label: t('roles.detail.members.columns.member'),
        render: (_, member) => (
          <div className="flex min-w-0 items-center gap-3">
            {isPlaceholderProfile(member) ? (
              <Avatar size="lg" placeholder alt={member.displayName} />
            ) : (
              <Avatar
                size="lg"
                initials={getInitials(member.displayName)}
                alt={member.displayName}
                className={getAvatarColorClass(member.displayName)}
              />
            )}
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium text-content">{member.displayName}</span>
              <span className="truncate text-xs text-content-muted">{member.email}</span>
            </div>
          </div>
        ),
      },
      {
        key: 'jobTitle',
        label: t('roles.detail.members.columns.jobTitle'),
        render: (jobTitle) => formatCell(jobTitle),
      },
      {
        key: 'mfaEnabled',
        label: t('roles.detail.members.columns.mfa'),
        render: (enabled, member) =>
          isPlaceholderProfile(member) ? (
            formatCell(null)
          ) : (
            <Badge variant={enabled ? 'success' : 'warning'}>{t(enabled ? 'members.mfa.on' : 'members.mfa.off')}</Badge>
          ),
      },
      {
        key: 'status',
        label: t('roles.detail.members.columns.status'),
        render: (status) => <StatusBadge status={status} />,
      },
      {
        key: 'id',
        label: '',
        render: (_, member) => (
          <Can permission={PERMISSIONS.ROLES_MANAGE}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={Trash2}
              aria-label={t('roles.detail.members.removeAria', { name: member.displayName })}
              onClick={() => setToRemove(member)}
            />
          </Can>
        ),
      },
    ],
    [t],
  );

  let body;
  if (isLoading) body = <Skeleton height="160px" borderRadius="12px" />;
  else if (error) body = <StatusMessage icon={TriangleAlert} title={t('roles.detail.members.loadError')} />;
  else if (!members.length) {
    body = (
      <StatusMessage
        icon={Users}
        title={t('roles.detail.members.empty.title')}
        subtitle={t('roles.detail.members.empty.subtitle')}
      />
    );
  } else {
    body = (
      <div className="overflow-x-auto lg:overflow-visible" data-testid="role-members">
        <DataTable columns={columns} data={members} minTableWidth={640} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-content">{t('roles.detail.members.count', { count: role.memberCount })}</p>
        <Can permission={PERMISSIONS.ROLES_MANAGE}>
          <Button variant="ghost" size="sm" onClick={openAssign}>
            {t('roles.detail.members.assign.action')}
          </Button>
        </Can>
      </div>
      {body}
      {isAssigning ? (
        <AssignRoleMembersModal role={role} memberIds={memberIds} onAssign={handleAssign} onClose={closeAssign} />
      ) : null}
      <ConfirmActionModal
        isOpen={Boolean(toRemove)}
        icon={Shield}
        iconTone="Alert"
        title={t('roles.detail.members.removeModal.title', { role: role.name })}
        message={t('roles.detail.members.removeModal.message', { name: toRemove?.displayName ?? '' })}
        confirmLabel={t('roles.detail.members.removeModal.confirm')}
        confirmVariant="danger"
        onClose={closeRemove}
        onConfirm={handleRemove}
      />
    </div>
  );
}

export default memo(RoleMembersTab);
